import React, {Component} from 'react';
import {connect} from 'react-redux';
import moment from 'moment-timezone';

import {Button} from 'primereact/button';
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';

import {Panel} from 'primereact/panel';

import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';
import StatusDropdown from '~/frontend/src/components/base/shared/field/StatusDropdown';
import TimezoneDropdown from '~/frontend/src/components/base/shared/field/TimezoneDropdown';
import TimestampField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimestampField';

import {formatDbTimestamp} from '~/shared/src/utilities/valueFormaters';
import {convertToDate} from '~/shared/src/utilities/valueConverters';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';

import {StoreState} from '~/frontend/src/store';

const defaultProps = {

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Should the field marked if dirty.
     */
    markDirty: false as boolean,

    /**
     * The CSS class name(s) to add to the component.
     */
    className: '' as string,

    /**
     * Should the menu button rendered with a label <strong>or</strong> not.
     */
    labeled: true as boolean
};

type DefaultProps = Readonly<typeof defaultProps>;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id : string;

    /**
     * The status to filter data for.
     */
    status : Status;

    /**
     * The timestamp to filter data at.
     */
    timestamp : string;

    /**
     * The timezone to filter data at.
     */
    timezone : string;

    tabIndex? : number;

    minDate?: Date;

    /**
     * Callback to invoke if any options value has changed.
     *
     * @param {String} status
     * @param {String} timezone
     * @param {String} timestamp
     */
    onChange : ( status : Status, timezone : string, timestamp : string ) => void;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps;

type State = {
    status : Status;
    selectedStatus : Status;
    timezone : string;
    selectedTimezone : string;
    timestamp : string;
    selectedTimestamp : string;
};

/**
 * Menu for options selection
 */
class AdvancedSearchOptionsMenu extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    contentButton? : ContentButtonType | null;
    justInteracted : boolean = false;

    originalValues = {
        status   : 'active' as Status,
        timestamp: 'now' as string,
        timezone : 'Europe/Berlin' as string
    };

    constructor( props : AllProps ) {
        super( props );

        this.state = {
            status           : props.status,
            selectedStatus   : props.status,
            timezone         : props.timezone,
            selectedTimezone : props.timezone,
            timestamp        : props.timestamp,
            selectedTimestamp: props.timestamp
        };

        // bind event handlers
        this.onContentButtonCollapse = this.onContentButtonCollapse.bind( this );
        this.onApplyButtonClick = this.onApplyButtonClick.bind( this );
        this.onCancelButtonClick = this.onCancelButtonClick.bind( this );
        this.onStatusDropdownChange = this.onStatusDropdownChange.bind( this );
        this.onTimezoneDropdownChange = this.onTimezoneDropdownChange.bind( this );
        this.onTimestampCheckboxChange = this.onTimestampCheckboxChange.bind( this );
        this.onTimestampFieldChange = this.onTimestampFieldChange.bind( this );
    }

    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const retVal : Partial<State> = {};

        if ( props.status !== state.status ) {
            retVal.status = props.status;
            retVal.selectedStatus = props.status;
        }

        if ( props.timezone !== state.timezone ) {
            retVal.timezone = props.timezone;
            retVal.selectedTimezone = props.timezone;
        }

        if ( props.timestamp !== state.timestamp ) {
            retVal.timestamp = props.timestamp;
            retVal.selectedTimestamp = props.timestamp;
        }

        return retVal;
    }

    /**
     * Handler for OverlayPanel show event.
     *
     * Resets the internal values to the outer ones (discard changes).
     *
     * @private
     */
    onContentButtonCollapse( event : React.MouseEvent, isOutsideClicked : boolean, eventPhase : 'beforeClick' | 'afterClick' | 'collapse' ) {
        let retVal = !this.justInteracted && isOutsideClicked;

        this.justInteracted = false;

        if ( retVal ) {
            const timestampOverlay = document.getElementById( `${this.props.uid}_timestamp_panel` );

            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            if ( timestampOverlay?.isSameNode( event.target as Node ) || timestampOverlay?.contains( event.target as Node ) ) {
                retVal = false;
            }
        }

        if ( retVal && eventPhase !== 'beforeClick' ) {
            this.setState( {
                selectedStatus   : this.props.status,
                selectedTimezone : this.props.timezone,
                selectedTimestamp: this.props.timestamp
            } );
        }

        return retVal;
    }

    /**
     * Handler for apply Button click event.
     *
     * Applies the changes of the internal values to the outer ones.
     *
     * @private
     */
    onApplyButtonClick( event : React.MouseEvent ) {
        this.justInteracted = false;

        this.props.onChange( this.state.selectedStatus, this.state.selectedTimezone, this.state.selectedTimestamp );

        this.contentButton?.collapse( event );
    }

    /**
     * Handler for cancel Button click event.
     *
     * Just hide the OverlayPanel.
     *
     * @private
     */
    onCancelButtonClick( event : React.MouseEvent ) {
        this.justInteracted = false;

        this.contentButton?.collapse( event );
    }

    /**
     * Handler for StatusDropdown change event.
     *
     * Update the internal value to the one from the input.
     *
     * @private
     */
    onStatusDropdownChange( status : Status ) {
        this.justInteracted = true;

        this.setState( { selectedStatus: status } );
    }

    /**
     * Handler for TimezoneDropdown change event.
     *
     * Update the internal value to the one from the input.
     *
     * @private
     */
    onTimezoneDropdownChange( timezone : string ) {
        this.justInteracted = true;

        this.setState( { selectedTimezone: timezone } );
    }

    /**
     * Handler for Checkbox change event.
     *
     * Update the internal value to the one from the input.
     *
     * @private
     */
    onTimestampCheckboxChange( event : CheckboxChangeEvent ) {
        let now;

        if ( event.checked && this.state.selectedTimestamp === 'now' ) {
            // get current date in local timezone
            now = moment();
            // output
            now = formatDbTimestamp( now );

            this.setState( { selectedTimestamp: now } );
        }
        else if ( !event.checked && this.state.selectedTimestamp !== 'now' ) {
            this.setState( { selectedTimestamp: 'now' } );
        }
    }

    /**
     * Handler for TimestampField change event.
     *
     * Update the internal value to the one from the input.
     *
     * @param {Date | undefined} value
     *
     * @private
     */
    onTimestampFieldChange( value : Date | undefined, e : any ) {
        this.justInteracted = true;

        if ( value ) {
            this.setState( { selectedTimestamp: formatDbTimestamp( value ) } );
        }
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  id,
                  rawMode,
                  showAdditionalRawNames,
                  markDirty,
                  className,
                  labeled,
                  tabIndex,
            minDate
              }                       = this.props,
              now                     = moment().toDate(),
              timestampFieldValue     = this.state.selectedTimestamp === 'now' ? now : convertToDate( this.state.selectedTimestamp, 'TIMESTAMP' ),
              timestampFieldDisabled  = this.state.selectedTimestamp === 'now',
              timestampFieldOrigValue = this.originalValues.timestamp === 'now' ? now : convertToDate( this.originalValues.timestamp, 'TIMESTAMP' ),
              buttonClassNames        = [ 'p-button-text', className ];

        if ( markDirty && ( this.state.status !== this.originalValues.status || this.state.timestamp !== this.originalValues.timestamp || this.state.timezone !== this.originalValues.timezone ) ) {
            buttonClassNames.push( 'yn-dirty-border' );
        }

        return <ContentButton
            id={id}

            ref={element => this.contentButton = element}

            horizontalContentAlign="right"
            verticalContentAlign="bottom"
            rounded={labeled
                ? false
                : true}
            text={true}
            label={labeled
                ? __( 'YN.base.finder.advancedSearchOptions.label' )
                : undefined}
            className={`yn-toolbar-button ${buttonClassNames.join( ' ' )} ${className}`}
            contentClassName="yn-contentbutton-overlay-content-page-menu"
            tabIndex={tabIndex}

            onCollapse={this.onContentButtonCollapse}

            content={
                <Panel
                    id={`${this.props.uid}_advancedSearchOptionsMenu`}
                    style={{
                        width: '25rem'
                    }}
                    pt={{
                        content: {
                            style: {
                                border: 'none'
                            }
                        }
                    }}
                >
                    {!labeled &&
                        <label
                            className="yn-menu-title-label block"
                        >
                            {__( 'YN.base.finder.advancedSearchOptions.label' )}
                        </label>
                    }

                    <StatusDropdown
                        uid={this.props.uid}

                        value={this.state.selectedStatus}
                        originalValue={this.originalValues.status}
                        mode="onlyActiveAndAll"
                        rawMode={rawMode}
                        showAdditionalRawNames={showAdditionalRawNames}
                        markDirty={markDirty}
                        onChange={this.onStatusDropdownChange}
                        label={__( 'YN.base.finder.status.label' )}
                        style={{ width: '100%' }}
                    />

                    <TimezoneDropdown
                        uid={this.props.uid}

                        value={this.state.selectedTimezone}
                        originalValue={this.originalValues.timezone}
                        markDirty={markDirty}
                        onChange={this.onTimezoneDropdownChange}
                        label={__( 'YN.base.finder.timezone.label' )}
                        style={{ width: '100%' }}
                    />

                    <div className="field">
                        <label
                            htmlFor={`${this.props.uid}_timestampField`}
                            className="block"
                        >
                            {__( 'YN.base.finder.timestamp.label' )}
                        </label>
                        <Checkbox
                            inputId={`${this.props.uid}_timestampField_checkbox`}
                            onChange={this.onTimestampCheckboxChange}
                            checked={!timestampFieldDisabled}
                            className="mr-2"
                            style={{ verticalAlign: 'top' }}
                        />
                        <div id={`${this.props.uid}_timestampField_wrapper`}
                             style={{ display: 'inline-block', width: 'calc(100% - 20px - 0.5rem)' }}>
                            <TimestampField
                                uid={this.props.uid}
                                id={`${this.props.uid}_timestamp`}

                                timezone={this.state.selectedTimezone}

                                value={timestampFieldValue ? timestampFieldValue : undefined}
                                originalValue={timestampFieldOrigValue ? timestampFieldOrigValue : undefined}
                                markDirty={markDirty}
                                disabled={timestampFieldDisabled}
                                onChange={this.onTimestampFieldChange}
                                minDate={minDate}
                                maxDate={new Date()}
                                labelWidth={0}
                                infoTextLayout="hidden"
                                classNames={[ 'mb-0' ]}
                                required
                                appendToElementId="document"
                            />
                        </div>
                    </div>

                    <hr className="mt-3 mb-3 yn-ruler"/>

                    <div className="flex justify-content-end">
                        <Button
                            id={`${uid}_advanced_options_apply`}
                            label={__( 'YN.base.common.apply' )}
                            onClick={this.onApplyButtonClick}
                            className="mr-3 p-button-outlined yn-button-defaultwidth text-center"
                        />
                        <Button
                            id={`${uid}_advanced_options_cancel`}
                            label={__( 'YN.base.common.cancel' )}
                            onClick={this.onCancelButtonClick}
                            className="p-button-secondary p-button-outlined yn-button-defaultwidth text-center"
                        />
                    </div>
                </Panel>
            }
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect( mapStateToProps, null )( AdvancedSearchOptionsMenu );
